import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserIcon, MailIcon, KeyIcon, CheckIcon, AlertIcon, CalendarIcon, ClockIcon, ShieldIcon, RefreshIcon } from '../components/common/Icons';

interface ProfileFormData {
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const ProfilePage: React.FC = () => {
  const { user, authFetch } = useAuth();

  const [formData, setFormData] = useState<ProfileFormData>({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'profile' | 'password'>('profile');
  const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      // Clear any existing timeout
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      // Set new timeout
      successTimeoutRef.current = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    }
    // Cleanup on unmount
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
    };
  }, [successMessage]);

  // Format date for display
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Not available';
    }
  };

  // Format datetime for display (includes time)
  const formatDateTime = (dateString?: string | null): string => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Never';
    }
  };

  // Get role display label
  const getRoleLabel = (role?: string): string => {
    if (!role) return 'User';
    const roleMap: Record<string, string> = {
      admin: 'Administrator',
      super_admin: 'Super Administrator',
      user: 'User',
      viewer: 'Viewer',
    };
    return roleMap[role.toLowerCase()] || role.charAt(0).toUpperCase() + role.slice(1);
  };

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
      }));
    }
  }, [user]);

  const validateProfileForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name, formData.email]);

  const validatePasswordForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain uppercase, lowercase, and number';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.currentPassword, formData.newPassword, formData.confirmPassword]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateProfileForm()) return;

    setIsLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const response = await authFetch('/api/auth/me', {
        method: 'PUT',
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
        }),
      });

      if (response.ok) {
        setSuccessMessage('Profile updated successfully');
      } else {
        const data = await response.json();
        setErrorMessage(data.detail || 'Failed to update profile');
      }
    } catch (error) {
      setErrorMessage('An error occurred while updating profile');
      console.error('Profile update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    setIsLoading(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      const response = await authFetch('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
        }),
      });

      if (response.ok) {
        setSuccessMessage('Password changed successfully');
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
      } else {
        const data = await response.json();
        setErrorMessage(data.detail || 'Failed to change password');
      }
    } catch (error) {
      setErrorMessage('An error occurred while changing password');
      console.error('Password change error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = () => {
    if (user?.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0][0].toUpperCase();
    }
    return (user?.email || 'U')[0].toUpperCase();
  };

  return (
    <div className="profile-page">
      <div className="page-header">
        <h1 className="page-title">My Profile</h1>
        <p className="page-subtitle">Manage your account settings and preferences</p>
      </div>

      <div className="profile-content">
        {/* Profile Card */}
        <div className="profile-card">
          <div className="profile-avatar-section">
            <div className="profile-avatar-large">
              <span>{getInitials()}</span>
            </div>
            <div className="profile-avatar-info">
              <h2>{user?.name || 'User'}</h2>
              <p>{user?.email}</p>
              <span className="profile-role-badge">{user?.role || 'User'}</span>
            </div>
          </div>
        </div>

        {/* Account Information Section */}
        <div className="profile-section account-info">
          <h3>Account Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="label">
                <CalendarIcon size={14} />
                Member Since
              </span>
              <span className="value">{formatDate((user as unknown as Record<string, string | undefined>)?.created_at)}</span>
            </div>
            <div className="info-item">
              <span className="label">
                <ClockIcon size={14} />
                Last Login
              </span>
              <span className="value">{formatDateTime((user as unknown as Record<string, string | undefined>)?.last_login)}</span>
            </div>
            <div className="info-item">
              <span className="label">
                <CheckIcon size={14} />
                Status
              </span>
              <span className="value">
                <span className="status-badge active">Active</span>
              </span>
            </div>
            <div className="info-item">
              <span className="label">
                <ShieldIcon size={14} />
                Role
              </span>
              <span className="value">
                <span className="role-badge">{getRoleLabel(user?.role)}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Section Tabs */}
        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
          >
            <UserIcon size={18} />
            <span>Profile Information</span>
          </button>
          <button
            className={`profile-tab ${activeSection === 'password' ? 'active' : ''}`}
            onClick={() => setActiveSection('password')}
          >
            <KeyIcon size={18} />
            <span>Change Password</span>
          </button>
        </div>

        {/* Messages */}
        {successMessage && (
          <div className="profile-message success">
            <CheckIcon size={18} />
            <span>{successMessage}</span>
          </div>
        )}
        {errorMessage && (
          <div className="profile-message error">
            <AlertIcon size={18} />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Profile Form */}
        {activeSection === 'profile' && (
          <form className="profile-form" onSubmit={handleProfileSubmit}>
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                <UserIcon size={16} />
                <span>Display Name</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className={`input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your name"
              />
              {errors.name && <span className="form-error">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <MailIcon size={16} />
                <span>Email Address</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                className={`input ${errors.email ? 'error' : ''}`}
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
              />
              {errors.email && <span className="form-error">{errors.email}</span>}
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <RefreshIcon size={16} className="spinner" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Password Form */}
        {activeSection === 'password' && (
          <form className="profile-form" onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label htmlFor="currentPassword" className="form-label">
                <KeyIcon size={16} />
                <span>Current Password</span>
              </label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                className={`input ${errors.currentPassword ? 'error' : ''}`}
                value={formData.currentPassword}
                onChange={handleInputChange}
                placeholder="Enter current password"
              />
              {errors.currentPassword && <span className="form-error">{errors.currentPassword}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">
                <KeyIcon size={16} />
                <span>New Password</span>
              </label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                className={`input ${errors.newPassword ? 'error' : ''}`}
                value={formData.newPassword}
                onChange={handleInputChange}
                placeholder="Enter new password"
              />
              {errors.newPassword && <span className="form-error">{errors.newPassword}</span>}
              <span className="form-hint">
                Must be at least 8 characters with uppercase, lowercase, and number
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">
                <KeyIcon size={16} />
                <span>Confirm New Password</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className={`input ${errors.confirmPassword ? 'error' : ''}`}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm new password"
              />
              {errors.confirmPassword && <span className="form-error">{errors.confirmPassword}</span>}
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <RefreshIcon size={16} className="spinner" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
